// Encoder Code 39 — dipakai halaman Master Barang untuk menggambar & mencetak
// barcode. Ditulis sendiri (bukan library npm) supaya tidak menambah dependensi
// pada build frontend yang sudah berat, dan supaya barcode bisa dirender offline
// tanpa CDN (CSP artifact/nginx tidak mengizinkan aset eksternal).
//
// Code 39 dipilih karena tabel polanya kecil & bisa diverifikasi secara
// struktural (lihat validateTable di bawah): setiap karakter = 9 elemen
// (5 bar + 4 spasi, selang-seling dimulai bar) dengan tepat 3 elemen lebar.
// Format: *DATA* — karakter '*' hanya sebagai start/stop, tidak ikut terbaca.

// n = elemen sempit, w = elemen lebar. Urutan: bar, spasi, bar, spasi, ...
const PATTERNS = {
  "0": "nnnwwnwnn",
  "1": "wnnwnnnnw",
  "2": "nnwwnnnnw",
  "3": "wnwwnnnnn",
  "4": "nnnwwnnnw",
  "5": "wnnwwnnnn",
  "6": "nnwwwnnnn",
  "7": "nnnwnnwnw",
  "8": "wnnwnnwnn",
  "9": "nnwwnnwnn",
  A: "wnnnnwnnw",
  B: "nnwnnwnnw",
  C: "wnwnnwnnn",
  D: "nnnnwwnnw",
  E: "wnnnwwnnn",
  F: "nnwnwwnnn",
  G: "nnnnnwwnw",
  H: "wnnnnwwnn",
  I: "nnwnnwwnn",
  J: "nnnnwwwnn",
  K: "wnnnnnnww",
  L: "nnwnnnnww",
  M: "wnwnnnnwn",
  N: "nnnnwnnww",
  O: "wnnnwnnwn",
  P: "nnwnwnnwn",
  Q: "nnnnnnwww",
  R: "wnnnnnwwn",
  S: "nnwnnnwwn",
  T: "nnnnwnwwn",
  U: "wwnnnnnnw",
  V: "nwwnnnnnw",
  W: "wwwnnnnnn",
  X: "nwnnwnnnw",
  Y: "wwnnwnnnn",
  Z: "nwwnwnnnn",
  "-": "nwnnnnwnw",
  ".": "wwnnnnwnn",
  " ": "nwwnnnwnn",
  $: "nwnwnwnnn",
  "/": "nwnwnnnwn",
  "+": "nwnnnwnwn",
  "%": "nnnwnwnwn",
  "*": "nwnnwnwnn", // start/stop
};

// Pemeriksaan struktural tabel di atas. Code 39 punya invarian ketat: 40 pola
// "biasa" = 2 bar lebar (dari 5) x 1 spasi lebar (dari 4), ditambah 4 pola
// khusus ($ / + %) = 0 bar lebar x 3 spasi lebar. Total 44, semuanya unik.
// Salah ketik satu huruf hampir pasti melanggar invarian ini, jadi kekeliruan
// tabel ketahuan saat modul dimuat, bukan saat barcode gagal di-scan.
const validateTable = () => {
  const seen = new Set();
  for (const [char, pat] of Object.entries(PATTERNS)) {
    if (pat.length !== 9) return `pola ${char} bukan 9 elemen`;
    if (seen.has(pat)) return `pola ${char} duplikat`;
    seen.add(pat);
    let wideBar = 0;
    let wideSpace = 0;
    for (let i = 0; i < 9; i++) {
      if (pat[i] !== "w") continue;
      if (i % 2 === 0) wideBar++;
      else wideSpace++;
    }
    const normal = wideBar === 2 && wideSpace === 1;
    const khusus = wideBar === 0 && wideSpace === 3;
    if (!normal && !khusus) return `pola ${char} melanggar invarian Code 39`;
  }
  if (Object.keys(PATTERNS).length !== 44) return "jumlah karakter bukan 44";
  return null;
};

const TABLE_ERROR = validateTable();
if (TABLE_ERROR && process.env.NODE_ENV === "development") {
  // Sengaja hanya log: barcode rusak lebih baik ketahuan di konsol dev daripada
  // menjatuhkan halaman master barang di produksi.
  console.error("[code39] tabel tidak valid:", TABLE_ERROR);
}

// Karakter yang bisa diwakili Code 39. Huruf kecil dinaikkan jadi kapital.
export const sanitizeCode39 = (value) =>
  (value || "")
    .toString()
    .toUpperCase()
    .split("")
    .filter((ch) => ch !== "*" && PATTERNS[ch] !== undefined)
    .join("");

export const isValidCode39 = (value) => {
  const raw = (value || "").toString().toUpperCase();
  if (!raw) return false;
  return raw.split("").every((ch) => ch !== "*" && PATTERNS[ch] !== undefined);
};

// Menghasilkan SVG barcode sebagai string.
//   narrow  : lebar elemen sempit (px). Elemen lebar = narrow * ratio.
//   height  : tinggi batang (px).
//   showText: cetak teks kode di bawah barcode.
export const code39Svg = (
  value,
  { narrow = 2, ratio = 3, height = 60, showText = true, fontSize = 12 } = {},
) => {
  const data = sanitizeCode39(value);
  if (!data) return "";

  const chars = `*${data}*`.split("");
  const wide = narrow * ratio;

  let x = 0;
  const bars = [];
  chars.forEach((ch, idx) => {
    const pat = PATTERNS[ch];
    for (let i = 0; i < 9; i++) {
      const w = pat[i] === "w" ? wide : narrow;
      // Elemen indeks genap = bar (hitam), ganjil = spasi (putih).
      if (i % 2 === 0) {
        bars.push(`<rect x="${x}" y="0" width="${w}" height="${height}" fill="#000"/>`);
      }
      x += w;
    }
    // Jeda antar-karakter selebar satu elemen sempit (wajib di Code 39).
    if (idx < chars.length - 1) x += narrow;
  });

  const totalWidth = x;
  const textHeight = showText ? fontSize + 6 : 0;
  const totalHeight = height + textHeight;
  const text = showText
    ? `<text x="${totalWidth / 2}" y="${height + fontSize + 2}" font-family="monospace" font-size="${fontSize}" text-anchor="middle" fill="#000">${data}</text>`
    : "";

  // style max-width/height:auto — barcode panjang (12+ digit) melebihi lebar
  // modal/label kalau ukurannya dipatok. viewBox membuat penyusutan tetap tajam.
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}" style="max-width:100%;height:auto;display:block">${bars.join("")}${text}</svg>`;
};

// Kode barcode baru: 12 digit, diawali "2" (rentang yang lazim dipakai untuk
// penomoran internal, bukan produk ritel), lalu YYMMDD, lalu 5 digit acak.
export const generateBarcodeValue = () => {
  const d = new Date();
  const yy = d.getFullYear().toString().slice(-2);
  const mm = (d.getMonth() + 1).toString().padStart(2, "0");
  const dd = d.getDate().toString().padStart(2, "0");
  const rand = Math.floor(Math.random() * 100000)
    .toString()
    .padStart(5, "0");
  return `2${yy}${mm}${dd}${rand}`;
};

export default code39Svg;
